import { motion } from "framer-motion";
import cookies from "js-cookie";
import React, { FC, memo, useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Button } from "@/components/Button";
import { ANIMATE_OPACITY_OUT, COOKIES_INFO_ANIMATE, TOPBAR_TRANSITION } from "@/core/animations";
import { MEDIA_SIZE_SM } from "@/core/media";
import { ISessionUser } from "@/data/session";
import { Flex, Icon, Text } from "@/eworkitComponents";
import { IWithSessionProps, withSession } from "@/infrastructure/Session";
import { PublicAnchor } from "@/router/PublicAnchor";

import "./_cookies-info.scss";

const baseClass = "cookies-info";

const KEY = "cookie-policy-accepted-2020-01-15";

interface Props extends IWithSessionProps<ISessionUser> {}

export const CookiesInfoComponent: FC<Props> = memo(props => {
  const {
    session: { authenticated },
  } = props;
  const [isCookiesAccept, setCookiesAccept] = useState(cookies.get(KEY));
  const [triggerCloseAnimation, setTriggerCloseAnimation] = useState(false);
  const { ready } = useTranslation("public");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const onClick = useCallback(() => {
    setTriggerCloseAnimation(true);
    cookies.set(KEY, "1", { expires: 365 });
  }, [setTriggerCloseAnimation]);
  const handleAnimationComplete = useCallback(() => {
    if (triggerCloseAnimation) {
      setCookiesAccept("1");
    }
  }, [triggerCloseAnimation, setCookiesAccept]);
  if (!ready || authenticated || isCookiesAccept === "1") {
    return null;
  }

  return (
    <div className={baseClass}>
      <motion.div
        id="cookies-bar"
        animate={triggerCloseAnimation ? ANIMATE_OPACITY_OUT : COOKIES_INFO_ANIMATE}
        transition={TOPBAR_TRANSITION}
        onAnimationComplete={handleAnimationComplete}>
        <Flex
          padding={[isSmallScreen ? "sm" : "md"]}
          align="flex-start"
          justify="space-between"
          direction="row"
          className={`${baseClass}__content`}>
          <Flex>
            <Text color="inverted">
              <Trans ns="public" i18nKey="COOKIES.MESSAGE">
                <PublicAnchor
                  className="anchor anchor--variant-link anchor--display-inline anchor--size-md anchor--shape-default anchor"
                  target="_blank"
                  page="COOKIE-POLICY"
                />
              </Trans>
            </Text>
          </Flex>
          <Button onClick={onClick} variant="clean">
            <Icon icon="close" size="lg" className={`${baseClass}__icon`} />
          </Button>
        </Flex>
      </motion.div>
    </div>
  );
});

export const CookiesInfo = withSession(CookiesInfoComponent);
